<!--
*  TTTech nerve-management-system-frontend
*  Copyright(c) 2026. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->
<template>
  <v-app-bar id="iiotToolbarCoreToolbar" data-cy="iiotToolbarCoreToolbar" :height="60" flat>
    <div class="logo-wrapper d-flex" :class="[isLogoVisible ? 'logo-visible' : 'logo-hidden']">
      <v-btn v-if="isLogoVisible" class="logo mr-3" variant="text" to="/nodes">
        <img class="logo-img" data-cy="iiotToolbarLogo" src="/img/nerve.svg" alt="Nerve Logo" />
      </v-btn>
      <div class="menu">
        <StatusIconWithOverlay
          id="iiotToolbarDrawer"
          data-cy="iiotToolbarDrawer"
          title="Menu"
          base-icon="mdi-dock-left"
          :overlay-icon="appStore.drawer ? 'mdi-pin' : 'mdi-menu-left'"
          size="24"
          :overlay-size="appStore.drawer ? 12 : 22"
          :overlay-offset-x="4"
          :overlay-offset-y="appStore.drawer ? 4 : 3"
          :cutout-size="appStore.drawer ? 19 : 18"
          @click="toggleDrawer"
        />
      </div>
    </div>
    <v-toolbar-title id="iiotToolbarTitle" class="font-weight-bold ml-3" data-cy="iiotToolbarTitle">{{
      pageTitle
    }}</v-toolbar-title>
    <v-spacer />
    <div class="d-flex align-center">
      <v-tooltip v-if="serviceUnavailable.isUnavailable" location="left">
        <template #activator="{ props }">
          <v-icon id="iiotToolbarWarningIcon" color="red" class="mx-3" v-bind="props"> mdi-alert </v-icon>
        </template>
        {{ $t('errorMessages.svc_0004') }}{{ serviceUnavailable.service }}
      </v-tooltip>

      <v-menu location="bottom end" offset="13" content-class="toolbar-dropdown">
        <template #activator="{ props }">
          <v-btn id="iiotToolbarHelp" data-cy="iiotToolbarHelp" class="mr-2" icon size="36" rounded v-bind="props">
            <v-icon size="24">mdi-help-circle</v-icon>
          </v-btn>
        </template>
        <v-list class="toolbar-dropdown-list pa-0" data-cy="iiotToolbarHelpDropdown" density="compact">
          <v-list-item class="pa-3" data-cy="iiotToolbarDocumentation" href="https://docs.nerve.cloud/" target="_blank">
            <template #prepend>
              <v-icon size="20">mdi-book-open-variant</v-icon>
            </template>
            <v-list-item-title class="text-body-2">{{ t('toolbar.menus.documentation') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            class="pa-3"
            data-cy="iiotToolbarDownloadConnectionManager"
            @click="goToDownloadConnectionManager"
          >
            <template #prepend>
              <v-icon size="20">mdi-download</v-icon>
            </template>
            <v-list-item-title class="text-body-2">
              {{ t('toolbar.menus.downloadConnectionManager') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-menu
        v-model="userMenuOpen"
        location="bottom end"
        offset="13"
        content-class="toolbar-dropdown"
        :close-on-content-click="false"
      >
        <template #activator="{ props }">
          <v-btn id="iiotToolbarAvatar" class="mr-4" data-cy="iiotToolbarAvatar" icon size="36" rounded v-bind="props">
            <v-avatar rounded size="36" class="custom-avatar">
              <v-img v-if="avatarUrl" :src="avatarUrl" alt="Avatar" />
              <span v-else data-cy="iiotToolbarAvatarInitials">{{ initials }}</span>
            </v-avatar>
          </v-btn>
        </template>
        <v-list class="toolbar-dropdown-list pa-0" data-cy="iiotToolbarUserDropdown" density="compact">
          <v-list-item class="user-info-item pa-3" data-cy="iiotToolbarUserInfo">
            <template #prepend>
              <v-icon size="36">mdi-badge-account</v-icon>
            </template>
            <v-list-item-title class="font-weight-bold">
              {{ authUser.firstName }} {{ authUser.lastName }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-body-2">
              {{ authUser.username }}
            </v-list-item-subtitle>
          </v-list-item>
          <v-divider />
          <v-list-item
            id="iiotToolbarUserSetting"
            data-cy="iiotToolbarUserSetting"
            class="pa-3"
            :disabled="!canAccess('UI_USER_SETTINGS:VIEW')"
            @click="goToSettings"
          >
            <template #prepend>
              <v-icon size="20">mdi-cog</v-icon>
            </template>
            <v-list-item-title class="text-body-2">{{ t('toolbar.menus.userSettings') }}</v-list-item-title>
          </v-list-item>
          <v-list-item id="iiotLogout" data-cy="iiotLogout" class="pa-3" @click="onLogout">
            <template #prepend>
              <v-icon size="20">mdi-logout</v-icon>
            </template>
            <v-list-item-title class="text-body-2">{{ t('toolbar.menus.logout') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </v-app-bar>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';

// Pinia Stores
import useAppStore from '@/store/modules/pinia/app';
import useAuthStore from '@/store/modules/pinia/auth';

// Business Logic Composables
import useAvatar from '@/composables/useAvatar';
import useAuth from '@/composables/useAuth';
import StatusIconWithOverlay from '@nerve/ui-components/components/shared/icons/StatusIconWithOverlay.vue';

// Initialize core tools
const route = useRoute();
const router = useRouter();
const { width } = useDisplay();
const appStore = useAppStore();
const authStore = useAuthStore();
const { t } = useI18n();

// Destructure composable methods
const { getAvatarURL } = useAvatar();
const { canAccess } = useAuth();

// --- Menu State ---
const userMenuOpen = ref(false);

// --- Responsive Breakpoints (Replaces old resize event listeners) ---
const isLogoVisible = computed(() => width.value > 800);

// --- User Data Handling ---
// Returns an empty object as fallback to prevent 'undefined' errors
const authUser = computed(() => authStore.user || {});
const serviceUnavailable = computed(() => authStore.serviceUnavailable || { isUnavailable: false });
const avatarUrl = computed(() => (authUser.value.profileImgURL ? getAvatarURL(authUser.value.profileImgURL) : ''));

// Computes user initials for the avatar placeholder
const initials = computed(() => {
  const f = authUser.value.firstName?.[0] || '';
  const l = authUser.value.lastName?.[0] || '';
  return (f + l).toUpperCase() || '?';
});

const pageTitle = computed(() => {
  const { meta } = route;
  if (!meta.title) return '';
  const key = meta.title(route);
  return t(key);
});

// --- Methods / Actions ---
const toggleDrawer = () => {
  appStore.toggleDrawer();
};

const goToSettings = () => {
  userMenuOpen.value = false;
  router.push({ name: 'User Settings' });
};

const onLogout = async () => {
  userMenuOpen.value = false;
  try {
    window.dispatchEvent(new CustomEvent('logout-required'));
  } catch (e) {
    console.error('Logout process failed:', e);
  }
};

const goToDownloadConnectionManager = () => {
  // close any open menus and navigate to the Downloads page
  userMenuOpen.value = false;
  router.push({ name: 'Download Nerve Connection Manager' });
};
</script>

<style lang="scss" scoped>
/* Toolbar Container Styling */
#iiotToolbarCoreToolbar {
  background: rgb(var(--v-theme-tertiary));
  color: rgb(var(--v-theme-secondary));
  border-radius: 0;
}

.logo-img {
  width: 100px;
  height: 28px;
}

.menu {
  display: flex;
  align-items: center;
  cursor: pointer;

  :deep(.status-icon-wrapper) {
    line-height: 0;
  }
}

/* Avatar Border and Background */
.custom-avatar {
  border: 1.5px solid white;
  background: rgb(var(--v-theme-tertiary)) !important;
  color: rgb(var(--v-theme-secondary)) !important;

  span {
    font-size: 14px;
    text-transform: uppercase;
  }
}

.user-info-item {
  min-height: 64px;
  cursor: default;
}

/* UI Cleanup: Removing unwanted default Vuetify overlays/ripples */
:deep(.v-btn__overlay),
:deep(.v-ripple__container) {
  display: none;
}

#iiotToolbarHelp {
  transition: background-color 0.15s ease;

  &:hover,
  &[aria-expanded='true'] {
    background-color: rgba(255, 255, 255, 0.15);
  }
}

#iiotToolbarAvatar {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    border: 0;
    background-color: rgba(255, 255, 255, 0.15);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
    z-index: 1;
  }

  &:hover::after,
  &[aria-expanded='true']::after {
    opacity: 1;
  }
}

#iiotToolbarCoreToolbar a {
  text-decoration: none;
}

.logo-wrapper {
  &.logo-visible {
    width: 240px;
  }
  &.logo-hidden {
    width: 56px;
    justify-content: center;
  }
}
</style>
